import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableHead,
  Toolbar, Typography, CircularProgress, TablePagination, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Box, FormControl, InputLabel, Select, InputAdornment
} from "@mui/material";
import { Delete, Block, CheckCircle, Info, Visibility, Search, FilterList } from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";
import {
  getUsers, suspendUser, activateUser, deleteUser, getUserDetails, updateUser
} from "../services/userService";
import FancyContextMenu, { ContextMenuAction } from "../components/FancyContextMenu";
import {
  StyledTableContainer,
  StyledTableCell,
  StyledTableRow,
  StyledPaper,
} from "../assets/styles/tableStyle"

type User = {
  id: number;
  firebaseUid: string;
  name?: string;
  email?: string;
  password?: string;
  phone: string;
  role: string;
  authCode?: string;
  createdAt?: string;
  updatedAt?: string;
  suspended: boolean;
  phoneVerified: boolean;
  emailVerified: boolean;
  gender?: string;
  assistanceRequired?: string;
  fcmToken?: string;
};

const booleanFields = ["suspended", "phoneVerified", "emailVerified"] as const;
const editableFields = ["name", "email", "phone", "authCode", "gender", "assistanceRequired"] as const;

const UserPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || "");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Modal State
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editData, setEditData] = useState<Partial<User>>({});
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const handleContextMenu = (event: React.MouseEvent, row: any) => {
    event.preventDefault();
    setSelectedRow(row);
    setMenu({ top: event.clientY, left: event.clientX });
  };

  const handleCloseMenu = () => setMenu(null);

// Define your actions, you can add more as needed!
  const actions: ContextMenuAction[] = [
    {
      dividerBefore: true,
      label: "View Full Details",
      icon: <Visibility color="primary" fontSize="small" />,
      onClick: () => {
        if (selectedRow) navigate(`/user/${selectedRow.id}`);
      },
      color: "primary",
    },
    {
      label: "Edit / Quick View",
      icon: <Edit color="info" fontSize="small" />,
      onClick: () => handleRowClick(selectedRow.id),
      color: "info",
    },
    {
      label: selectedRow?.suspended ? "Activate" : "Suspend",
      icon: selectedRow?.suspended ? <CheckCircle color="success" fontSize="small" /> : <Block color="error" fontSize="small" />,
      onClick: () => selectedRow?.suspended ? handleActivate(selectedRow.id) : handleSuspend(selectedRow.id),
      color: selectedRow?.suspended ? "success" : "warning",
    },
    {
      label: "Show Rides",
      icon: <Info color="info" fontSize="small" />,
      onClick: () => {
        if (!selectedRow?.firebaseUid) return;
        window.location.href = `/rides?${selectedRow.role === 'driver' ? 'driverUid' : 'riderUid'}=${selectedRow.firebaseUid}`;
      },
      color: "info",
    },
    {
      label: "Show Wallet",
      icon: <Info color="info" fontSize="small" />,
      onClick: () => {
        if (!selectedRow?.firebaseUid) return;
        handleShowWalletDetails(selectedRow);
      },
      color: "info",
    },
    {
      dividerBefore: true,
      label: "Delete",
      icon: <Delete color="error" fontSize="small" />,
      onClick: () => handleDelete(selectedRow.id),
      color: "error",
    },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users when list or search term changes
  useEffect(() => {
    let result = users;

    // 1. Search Filter
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(u =>
          u.firebaseUid.toLowerCase().includes(lowerTerm) ||
          u.name?.toLowerCase().includes(lowerTerm) ||
          u.email?.toLowerCase().includes(lowerTerm) ||
          u.phone.includes(lowerTerm)
      );
    }

    // 2. Role Filter
    if (roleFilter !== "all") {
      result = result.filter(u => u.role === roleFilter);
    }

    // 3. Status Filter
    if (statusFilter !== "all") {
      const isSuspended = statusFilter === 'suspended';
      result = result.filter(u => u.suspended === isSuspended);
    }

    setFilteredUsers(result);
    setPage(0); // Reset to first page on filter change
  }, [users, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getUsers();
      setUsers(res.data);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (id: number) => {
    setSelectedId(id);
    setDetailOpen(true);
    setEditLoading(true);
    try {
      const res = await getUserDetails(id);
      setSelectedUser(res.data);
      setEditData(res.data);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
    setSelectedUser(null);
    setEditData({});
  };

  const handleFieldChange = (field: keyof User, value: string | boolean) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedId) return;
    setEditLoading(true);
    try {
      // Only send editable fields
      const updateFields: any = {};
      [...editableFields, ...booleanFields].forEach((field) => {
        if (editData[field] !== undefined) updateFields[field] = editData[field];
      });
      await updateUser(selectedId, updateFields);
      await fetchUsers();
      setDetailOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const handleSuspend = async (id: number) => {
    setLoading(true);
    try { await suspendUser(id); await fetchUsers(); } finally { setLoading(false); }
  };

  const handleActivate = async (id: number) => {
    setLoading(true);
    try { await activateUser(id); await fetchUsers(); } finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try { await deleteUser(id); await fetchUsers(); } finally { setLoading(false); }
  };


  const handleShowWalletDetails = (row: any) => {
    if (!row?.firebaseUid) return;
    let query = "";
    if (row.role === "driver") {
      query = `?driverUid=${encodeURIComponent(row.firebaseUid)}`;
    } else {
      query = `?riderUid=${encodeURIComponent(row.firebaseUid)}`;
    }
    window.location.href = `/wallet-transactions${query}`;
  };

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
      <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6">User Management</Typography>

          {/* Controls Container */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search Input */}
            <TextField
                placeholder="Search Name, Email, Phone..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
            />

            {/* Role Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                  startAdornment={<FilterList fontSize="small" sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="rider">Rider</MenuItem>
                <MenuItem value="driver">Driver</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>

            {/* Clear Button */}
            {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') && (
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setSearchTerm("");
                      setRoleFilter("all");
                      setStatusFilter("all");
                    }}
                >
                  Clear
                </Button>
            )}
          </Box>
        </Toolbar>
        {loading ? (
            <CircularProgress sx={{ margin: 2 }} />
        ) : (
            <StyledTableContainer>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>ID</StyledTableCell>
                    <StyledTableCell>User UID</StyledTableCell>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>Email</StyledTableCell>
                    <StyledTableCell>Phone</StyledTableCell>
                    <StyledTableCell>Role</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Phone Verified</StyledTableCell>
                    <StyledTableCell>Email Verified</StyledTableCell>
                    <StyledTableCell>Gender</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user) => (
                      <StyledTableRow
                          key={user.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onContextMenu={e => handleContextMenu(e, user)}
                          onClick={() => handleRowClick(user.id)}
                      >
                        <StyledTableCell>{user.id}</StyledTableCell>
                        <StyledTableCell>{user.firebaseUid}</StyledTableCell>
                        <StyledTableCell>{user.name || "-"}</StyledTableCell>
                        <StyledTableCell>{user.email || "-"}</StyledTableCell>
                        <StyledTableCell>{user.phone}</StyledTableCell>
                        <StyledTableCell>
                          <Chip label={user.role} color="info" variant="outlined" size="small"/>
                        </StyledTableCell>
                        <StyledTableCell>
                          {user.suspended ? <Chip label="Suspended" color="error" size="small"/> : <Chip label="Active" color="success" size="small"/>}
                        </StyledTableCell>
                        <StyledTableCell>
                          {user.phoneVerified ? (
                              <CheckCircle color="success" fontSize="small" />
                          ) : (
                              <Block color="warning" fontSize="small" />
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          {user.emailVerified ? (
                              <CheckCircle color="success" fontSize="small" />
                          ) : (
                              <Block color="warning" fontSize="small" />
                          )}
                        </StyledTableCell>
                        <StyledTableCell>{user.gender || "-"}</StyledTableCell>
                      </StyledTableRow>
                  ))}
                  {paginatedUsers.length === 0 && !loading && (
                      <StyledTableRow>
                        <StyledTableCell colSpan={10} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>No users found matching filters.</Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={filteredUsers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
              />
            </StyledTableContainer>
        )}

        {/* Quick View / Edit Modal */}
        <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="md">
          <DialogTitle>User Quick View</DialogTitle>
          <DialogContent>
            {editLoading ? (
                <CircularProgress />
            ) : selectedUser && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Add Navigation Button here */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Visibility />}
                        onClick={() => navigate(`/user/${selectedUser.id}`)}
                    >
                      Go to Full Details Page
                    </Button>
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mt: 1 }}>
                    {/* Fields ... */}
                    <TextField label="ID" value={selectedUser.id} fullWidth disabled />
                    <TextField label="Firebase UID" value={selectedUser.firebaseUid ?? ""} fullWidth disabled />
                    <TextField label="Created At" value={selectedUser.createdAt || ""} fullWidth disabled />
                    <TextField label="Updated At" value={selectedUser.updatedAt || ""} fullWidth disabled />
                    <TextField label="FCM Token" value={selectedUser.fcmToken ?? ""} fullWidth disabled />
                    <TextField label="Name" value={editData.name ?? ""} fullWidth onChange={e => handleFieldChange("name", e.target.value)} />
                    <TextField label="Email" value={editData.email ?? ""} fullWidth onChange={e => handleFieldChange("email", e.target.value)} />
                    <TextField label="Phone" value={editData.phone ?? ""} fullWidth onChange={e => handleFieldChange("phone", e.target.value)} />
                    <TextField
                        label="Gender"
                        value={editData.gender ?? ""}
                        fullWidth
                        onChange={e => handleFieldChange("gender", e.target.value)}
                    />
                    <TextField
                        label="Assistance Required"
                        value={editData.assistanceRequired ?? ""}
                        fullWidth
                        onChange={e => handleFieldChange("assistanceRequired", e.target.value)}
                    />
                    <TextField
                        label="Auth Code"
                        value={editData.authCode ?? ""}
                        fullWidth
                        onChange={e => handleFieldChange("authCode", e.target.value)}
                    />
                    <TextField select label="Suspended" value={editData.suspended ? "yes" : "no"} fullWidth onChange={e => handleFieldChange("suspended", e.target.value === "yes")}>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                    <TextField
                        select
                        label="Phone Verified"
                        value={editData.phoneVerified ? "yes" : "no"}
                        fullWidth
                        onChange={e => handleFieldChange("phoneVerified", e.target.value === "yes")}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                    <TextField
                        select
                        label="Email Verified"
                        value={editData.emailVerified ? "yes" : "no"}
                        fullWidth
                        onChange={e => handleFieldChange("emailVerified", e.target.value === "yes")}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Box>
                </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetail}>Close</Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={editLoading}>Save</Button>
          </DialogActions>
        </Dialog>
        <FancyContextMenu
            actions={actions}
            open={Boolean(menu)}
            onClose={handleCloseMenu}
            anchorPosition={menu}
            title={selectedRow?.name || selectedRow?.firebaseUid || "Actions"}
        />
      </StyledPaper>
  );
};

export default UserPage;
